import React, { Component } from "react";
import HelperClass from "../../config/helperClass";
import {
  getSingleResellerInvoice,
  getInvoicePayments,
  getSingleSalesOrder,
  getPaymentMethod,
} from "../../config/reseller_api_calls";
import { AGENT_PORTAL_URL } from "../../config/config";
import RecordPaymentModal from "./RecordPaymentModal";
import NotificationToast from "../Notification/NotificationToast";
import "./ViewInvoiceModal.css";

const auth = new HelperClass();

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtCurrency(val, decimals) {
  const n = Number(val);
  if (!Number.isFinite(n)) return "$0.00";
  const fixed = n.toFixed(decimals || 2);
  const [intPart, decPart] = fixed.split(".");
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return "$" + (decPart !== undefined ? withCommas + "." + decPart : withCommas);
}

function fmtDate(val) {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}-${dd}-${d.getFullYear()}`;
  } catch {
    return val;
  }
}

function fmtAddress(entity) {
  if (!entity) return "";
  return [
    entity.address,
    [entity.city, entity.state, entity.zip].filter(Boolean).join(", "),
    entity.country,
  ]
    .filter(Boolean)
    .join(" • ");
}

function getInvoiceBalance(invoice, amountPaid) {
  return Math.max(
    0,
    (Number(invoice && invoice.grand_total) || 0) -
      (Number(invoice && invoice.credit_applied) || 0) -
      (amountPaid !== undefined ? amountPaid : (Number(invoice && invoice.amount_paid) || 0)),
  );
}

function isOverdue(inv) {
  if (!inv || !inv.due_date) return false;
  if (inv.payment_status === "Paid") return false;
  return new Date(inv.due_date) < new Date();
}

// Display-only label: stored value stays "Pending"; UI shows "Unpaid".
function paymentStatusLabel(s) {
  return !s || s === "Pending" ? "Unpaid" : s;
}

// ─────────────────────────────────────────────────────────────────────────────

class ViewInvoiceModal extends Component {
  constructor(props) {
    super(props);
    this._decimals = Number(auth.getBillingDecimalPoint() || 2);
    this.state = {
      detailData: null,
      detailServices: [],
      detailDiscounts: [],
      detailTaxes: [],
      detailShipping: [],
      detailReseller: null,
      detailClient: null,
      detailComputed: null,
      detailLoading: false,
      detailError: "",
      viewModalTab: "customer",
      payHistory: [],
      showPayModal: false,
      payInvoice: null,
      orderTotal: null,
      orderContactPerson: null,
      orderContactEmail: null,
      paymentDetails: "",
      toastMsg: "",
      toastType: "success",
    };
  }

  componentDidUpdate(prevProps) {
    const { show, invoiceNo } = this.props;
    if (!prevProps.show && show && invoiceNo) {
      this.loadInvoice(invoiceNo);
    }
    if (prevProps.show && !show) {
      this.resetState();
    }
  }

  resetState = () => {
    this.setState({
      detailData: null,
      detailServices: [],
      detailDiscounts: [],
      detailTaxes: [],
      detailShipping: [],
      detailReseller: null,
      detailClient: null,
      detailComputed: null,
      detailLoading: false,
      detailError: "",
      viewModalTab: "customer",
      payHistory: [],
      showPayModal: false,
      payInvoice: null,
      orderTotal: null,
      orderContactPerson: null,
      orderContactEmail: null,
      paymentDetails: "",
      toastMsg: "",
      toastType: "success",
    });
  };

  loadInvoice = async (invoiceNo) => {
    this.setState({ detailLoading: true, detailError: "", viewModalTab: "customer" });
    try {
      const [invoiceResp, paymentsResp] = await Promise.all([
        getSingleResellerInvoice(auth.getAccount(), auth.getToken(), invoiceNo),
        getInvoicePayments(auth.getAccount(), auth.getToken(), invoiceNo),
      ]);
      const payload = invoiceResp && invoiceResp.data;
      if (
        payload &&
        (payload.status === 403 ||
          payload.errors === "authentication missing" ||
          payload.errors === "jwt expired")
      ) {
        auth.logout();
        return;
      }
      const paymentsPayload = paymentsResp && paymentsResp.data;
      const detailOk = payload && payload.status === 200 && payload.data;
      const invoiceData = detailOk ? payload.data.invoice || null : null;
      const hasOrderFetch = detailOk && invoiceData && invoiceData.orderno;

      this.setState({
        detailData: invoiceData,
        detailServices: detailOk ? payload.data.services || [] : [],
        detailDiscounts: detailOk ? payload.data.discounts || [] : [],
        detailTaxes: detailOk ? payload.data.taxes || [] : [],
        detailShipping: detailOk ? payload.data.shipping || [] : [],
        detailReseller: detailOk ? payload.data.reseller || null : null,
        detailClient: detailOk ? payload.data.client || null : null,
        detailComputed: detailOk ? payload.data.computed || null : null,
        detailLoading: !!hasOrderFetch,
        detailError: !detailOk
          ? (payload && payload.message) || "Failed to load invoice."
          : "",
        toastMsg: !detailOk ? (payload && payload.message) || "Failed to load invoice." : "",
        toastType: "error",
        payHistory:
          (paymentsPayload &&
            paymentsPayload.status === 200 &&
            paymentsPayload.data &&
            paymentsPayload.data.payments) ||
          [],
      });

      // Reseller's configured payment method/details for this invoice's order
      // (reuses the sales-order endpoint), shown in the modal + print only when unpaid.
      if (invoiceData && invoiceData.orderno) {
        try {
          const pmResp = await getPaymentMethod(auth.getAccount(), auth.getToken(), invoiceData.orderno);
          const pm = pmResp && pmResp.data;
          if (pm && pm.status === 200 && Array.isArray(pm.data) && pm.data.length > 0) {
            this.setState({ paymentDetails: pm.data[0].payment_details || "" });
          } else {
            this.setState({ paymentDetails: "" });
          }
        } catch {
          this.setState({ paymentDetails: "" });
        }
      }

      if (hasOrderFetch) {
        try {
          const orderResp = await getSingleSalesOrder(
            auth.getAccount(),
            auth.getToken(),
            invoiceData.orderno
          );
          const od = orderResp && orderResp.data;
          if (od && od.status === 200 && od.data && od.data[0]) {
            const fetched = Number(od.data[0].quote_total || od.data[0].order_total || 0);
            const update = { detailLoading: false };
            if (fetched > 0) update.orderTotal = fetched;
            if (od.data[0].contact_person) update.orderContactPerson = od.data[0].contact_person;
            if (od.data[0].contact_email) update.orderContactEmail = od.data[0].contact_email;
            this.setState(update);
          } else {
            this.setState({ detailLoading: false });
          }
        } catch (e) {
          this.setState({ detailLoading: false });
        }
      }
    } catch {
      this.setState({ detailLoading: false, detailError: "Failed to load invoice.", toastMsg: "Failed to load invoice.", toastType: "error" });
    }
  };

  handleClose = () => {
    this.resetState();
    if (this.props.onClose) this.props.onClose();
  };

  printInvoice = () => {
    const area = document.getElementById("inv-vm-print-area");
    if (!area) return;
    const w = window.open("", "_blank");
    w.document.write(`
      <html><head><title>Invoice</title>
      <style>
        body { font-family: Roboto, "Helvetica Neue", Arial, sans-serif; margin: 32px; color: #111; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 10px; border: 1px solid #ddd; font-size: 12px; }
        .navy { background: #0F3A70; color: white; }
      </style></head><body>`);
    console.log("inv-vm-print-area HTML:", area.innerHTML);
    w.document.write(area.innerHTML);
    w.document.write("</body></html>");
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  downloadInvoicePdf = async (invoiceRecord) => {
    const pdfPath = invoiceRecord && invoiceRecord.pdf_path;
    if (!pdfPath) {
      this.printInvoice();
      return;
    }
    const url = AGENT_PORTAL_URL + "files_data/rfq/" + pdfPath;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      document.body.appendChild(link);
      link.style = "display: none";
      link.href = downloadUrl;
      link.download = pdfPath;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      link.remove();
    } catch {
      this.printInvoice();
    }
  };

  openPayModal = (inv) => {
    this.setState({ showPayModal: true, payInvoice: inv });
  };

  closePayModal = () => {
    this.setState({ showPayModal: false, payInvoice: null });
  };

  handlePaymentSuccess = async () => {
    this.setState({ showPayModal: false, payInvoice: null });
    const { invoiceNo, onPaymentRecorded } = this.props;
    if (invoiceNo) await this.loadInvoice(invoiceNo);
    if (onPaymentRecorded) onPaymentRecorded();
  };

  handleFollowUp = () => {
    const { onFollowUp } = this.props;
    if (!onFollowUp) return;
    const { detailData, detailReseller, detailClient } = this.state;
    onFollowUp({ ...(detailData || {}), reseller: detailReseller, client: detailClient });
  };

  handleNotify = () => {
    const { onNotify } = this.props;
    if (!onNotify) return;
    const { detailData, detailReseller, detailClient } = this.state;
    onNotify({ ...(detailData || {}), reseller: detailReseller, client: detailClient });
  };

  // ── Sidebar ───────────────────────────────────────────────────────────────

  renderSidebar(inv) {
    const d = this._decimals;
    const { detailComputed, detailReseller, detailClient } = this.state;
    const { onFollowUp, onNotify } = this.props;
    const isPaid = inv.payment_status === "Paid";
    const grandTotal = Number(inv.grand_total) || 0;
    const amountPaid = Number(detailComputed && detailComputed.amount_paid != null ? detailComputed.amount_paid : inv.amount_paid) || 0;
    const balanceDue = Number(detailComputed && detailComputed.balance_due != null ? detailComputed.balance_due : getInvoiceBalance(inv, amountPaid));

    return (
      <div className="inv-vm-sidebar">
        <div className={`inv-vm-status-card${isPaid ? " paid" : ""}`}>
          <div className="inv-vm-status-label">
            {isPaid ? "Paid" : inv.payment_status === "Partial" ? "Partial" : "Unpaid"}
          </div>
          <div className="inv-vm-status-amount">
            {fmtCurrency(isPaid ? grandTotal : balanceDue, d)}
          </div>
          {!isPaid && (
            <div className="inv-vm-status-sub">{fmtCurrency(balanceDue, d)} outstanding</div>
          )}
        </div>

        <div className="inv-vm-sidebar-section">
          <div className="inv-vm-sidebar-row">
            <span>Subtotal</span>
            <span>{fmtCurrency(inv.sub_total, d)}</span>
          </div>
          {Number(inv.discount_total) > 0 && (
            <div className="inv-vm-sidebar-row">
              <span>Discount</span>
              <span style={{ color: "#1A7A4A" }}>−{fmtCurrency(inv.discount_total, d)}</span>
            </div>
          )}
          {Number(inv.tax_total) > 0 && (
            <div className="inv-vm-sidebar-row">
              <span>Tax</span>
              <span>{fmtCurrency(inv.tax_total, d)}</span>
            </div>
          )} 
          {Number(inv.shipping_total) > 0 && (
            <div className="inv-vm-sidebar-row">
              <span>Shipping</span>
              <span>{fmtCurrency(inv.shipping_total, d)}</span>
            </div>
          )}
          <div className="inv-vm-sidebar-row inv-vm-sidebar-total">
            <span>Grand Total</span>
            <span>{fmtCurrency(grandTotal, d)}</span>
          </div>
        </div>

        <div className="inv-vm-sidebar-meta">
          {[
            { label: "Invoice #", value: inv.invoice_no },
            { label: "Order #",   value: inv.orderno || "—" },
            { label: "Issued",    value: fmtDate(inv.invoice_date) },
            { label: "Due",       value: fmtDate(inv.due_date) },
            { label: "Period",    value: inv.billing_period || "—" },
            { label: "Terms",     value: inv.payment_terms || "Net 30" },
          ].map((m) => (
            <div key={m.label} className="inv-vm-meta-row">
              <span className="inv-vm-meta-label">{m.label}</span>
              <span className="inv-vm-meta-value">{m.value}</span>
            </div>
          ))}
        </div>

        <div className="inv-vm-sidebar-actions">
          {!isPaid && (
            <button className="btn btn-success" style={{ width: "100%" }} onClick={() => this.openPayModal(inv)}>
              ✓ Record Payment — {fmtCurrency(balanceDue, d)}
            </button>
          )}
          <button className="btn btn-primary" style={{ width: "100%" }} onClick={() => this.downloadInvoicePdf(inv)}>
            ↓ Download / Print PDF
          </button>
          {onFollowUp && (
            <button className="btn btn-secondary" style={{ width: "100%" }} onClick={this.handleFollowUp}>
              + Create Follow-up Invoice
            </button>
          )}
          {onNotify && (
            <button className="btn btn-white btn-dim btn-outline-primary" style={{ width: "100%" }} onClick={this.handleNotify}>
              ✉ Email Invoice to Client
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  render() {
    const { show, onFollowUp } = this.props;
    if (!show) return null;

    const {
      detailLoading,
      detailError,
      detailData,
      detailServices,
      detailDiscounts,
      detailTaxes,
      detailShipping,
      detailReseller,
      detailClient,
      detailComputed,
      viewModalTab,
      payHistory,
      showPayModal,
      payInvoice,
    } = this.state;

    if (detailLoading) {
      return (
        <div className="inv-vm-overlay" onClick={(e) => { if (e.target === e.currentTarget) this.handleClose(); }}>
          <div className="inv-vm-dialog">
            <div style={{ padding: "60px", textAlign: "center", color: "#6B6762" }}>
              <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} />
              Loading invoice…
            </div>
          </div>
        </div>
      );
    }

    if (detailError) {
      return (
        <>
          <div className="inv-vm-overlay" onClick={(e) => { if (e.target === e.currentTarget) this.handleClose(); }}>
            <div className="inv-vm-dialog">
              <div style={{ padding: "32px", textAlign: "center" }}>
                <button className="btn btn-secondary btn-sm" onClick={this.handleClose}>Close</button>
              </div>
            </div>
          </div>
          <NotificationToast
            message={this.state.toastMsg}
            type={this.state.toastType}
            onDismiss={() => this.setState({ toastMsg: "" })}
          />
        </>
      );
    }

    const inv = detailData;
    if (!inv) return null;

    const d = this._decimals;
    const isPaid = inv.payment_status === "Paid";
    const grandTotal = Number(inv.grand_total) || 0;
    const salesOrderTotal = this.state.orderTotal !== null ? this.state.orderTotal : grandTotal;
    const amountPaid = Number(detailComputed && detailComputed.amount_paid != null ? detailComputed.amount_paid : inv.amount_paid) || 0;
    const balanceDue = Number(detailComputed && detailComputed.balance_due != null ? detailComputed.balance_due : getInvoiceBalance(inv, amountPaid));
    const isOverdueInv = isOverdue(inv);
    const resellerName = (detailReseller && detailReseller.comp_name) || "Company";
    const resellerAddress = fmtAddress(detailReseller);
    const clientName =
      (detailClient && (detailClient.comp_name || detailClient.name)) ||
      inv.customer_name ||
      "—";
    const clientAddress = fmtAddress(detailClient);
    const clientEmail =
      (detailClient && detailClient.email) || inv.customer_email || "—";
    const resellerTaxId =
      detailReseller &&
      (detailReseller.tax_id ||
        detailReseller.tax_number ||
        detailReseller.vat_number ||
        detailReseller.ein);

    return (
      <>
        <div
          className="inv-vm-overlay"
          style={{ zIndex: 1045 }}
          onClick={(e) => { if (e.target === e.currentTarget) this.handleClose(); }}
        >
          <div className="inv-vm-dialog" onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div className="inv-vm-header">
              <div className="inv-vm-header-left">
                <span className="inv-vm-inv-no">{inv.invoice_no}</span>
                <span className="inv-vm-meta">
                  {inv.customer_name || "—"}
                  {inv.billing_period ? ` · ${inv.billing_period}` : ""}
                  {" · "}{fmtCurrency(grandTotal, d)} 
                </span>
              </div>
              <div className="inv-vm-tabs">
                {[
                  { key: "customer", label: "Customer Invoice" },
                  { key: "admin",    label: "Admin Summary" },
                  { key: "payments", label: "Payment Records" },
                ].map((t) => (
                  <button
                    key={t.key}
                    className={`inv-vm-tab${viewModalTab === t.key ? " active" : ""}`}
                    onClick={() => this.setState({ viewModalTab: t.key })}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="inv-vm-header-right">
                <button className="btn btn-secondary" onClick={() => this.downloadInvoicePdf(inv)}>
                  ↓ Download PDF
                </button>
                <button className="inv-vm-close" onClick={this.handleClose}>×</button>
              </div>
            </div>

            {/* Body */}
            <div className="inv-vm-body">

              {/* ── Customer Invoice tab ─────────────────────────────── */}
              {viewModalTab === "customer" && (
                <div className="inv-vm-layout">
                  <div className="inv-vm-doc" id="inv-vm-print-area">

                    <div className="inv-vm-pdf-doc-header">
                      <div className="inv-vm-pdf-brand">
                        {detailReseller && detailReseller.logo_url ? (
                          <img src={detailReseller.logo_url} alt={resellerName} className="inv-vm-pdf-logo-img" />
                        ) : (
                          <span className="inv-vm-pdf-brand-name">{resellerName}</span>
                        )}
                      </div>
                      <div className="inv-vm-pdf-title-centered">
                        {inv.invoice_type === "follow_up"
                          ? "Follow-up Invoice"
                          : inv.invoice_type === "partial"
                          ? "Partial Invoice"
                          : "Invoice"}
                      </div>
                      <div />
                    </div>

                    <div className="inv-vm-pdf-billing-section">
                      <div className="inv-vm-pdf-billing-left">
                        <div>
                          <div className="inv-vm-pdf-block-label">BILL TO</div>
                          <div className="inv-vm-pdf-block-name">{clientName}</div>
                          {detailClient && detailClient.name && (
                            <div className="inv-vm-pdf-block-line"><strong>Contact:</strong> {detailClient.name}</div>
                          )}
                          {clientAddress && (
                            <div className="inv-vm-pdf-block-line"><strong>Address:</strong> {clientAddress}</div>
                          )}
                          {detailClient && detailClient.phone && (
                            <div className="inv-vm-pdf-block-line"><strong>Phone:</strong> {detailClient.phone}</div>
                          )}
                          {clientEmail && (
                            <div className="inv-vm-pdf-block-line"><strong>Email:</strong> <span className="inv-vm-pdf-link">{clientEmail}</span></div>
                          )}
                        </div>
                        <div>
                          <div className="inv-vm-pdf-block-label">BILL FROM</div>
                          <div className="inv-vm-pdf-block-name">{resellerName}</div>
                          {resellerAddress && (
                            <div className="inv-vm-pdf-block-line"><strong>Address:</strong> {resellerAddress}</div>
                          )}
                          {detailReseller && detailReseller.reseller_phone && (
                            <div className="inv-vm-pdf-block-line"><strong>Phone:</strong> {detailReseller.reseller_phone}</div>
                          )}
                          {detailReseller && detailReseller.email && (
                            <div className="inv-vm-pdf-block-line"><strong>Email:</strong> <span className="inv-vm-pdf-link">{detailReseller.email}</span></div>
                          )}
                          {resellerTaxId && (
                            <div className="inv-vm-pdf-block-line"><strong>Tax ID:</strong> {resellerTaxId}</div>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="inv-vm-pdf-block-label">INVOICE DETAILS</div>
                        <table className="inv-vm-pdf-details-table">
                          <tbody>
                            {[
                              { label: "Invoice No",       value: inv.invoice_no, mono: true },
                              { label: "Order No",         value: inv.orderno || "—", mono: true },
                              { label: "Invoice Type",     value: inv.invoice_type === "follow_up" ? "Follow-up Invoice" : inv.invoice_type === "partial" ? "Partial Invoice" : "Standard Invoice", bold: true },
                              { label: "Payment Status",   value: paymentStatusLabel(inv.payment_status), statusKey: (inv.payment_status || "pending").toLowerCase() },
                              { label: "Issue Date",       value: fmtDate(inv.invoice_date) },
                              { label: "Due Date",         value: fmtDate(inv.due_date) },
                              { label: "Billing Period",   value: inv.billing_period || "—" },
                              { label: "Terms of Payment", value: inv.payment_terms || "Net 30" },
                              { label: "Currency",         value: inv.currency || "USD" },
                              { label: "Contact Person",   value: this.state.orderContactPerson || resellerName || "—" },
                              { label: "Email",            value: this.state.orderContactEmail || (detailReseller && (detailReseller.billing_email || detailReseller.email)) || "—", link: true },
                            ].map((row) => (
                              <tr key={row.label}>
                                <td className="inv-vm-pdf-dt-label">{row.label}</td>
                                <td className={`inv-vm-pdf-dt-value${row.bold ? " bold" : ""}${row.mono ? " mono" : ""}${row.statusKey ? " status-" + row.statusKey : ""}${row.link ? " link" : ""}`}>
                                  {row.value}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {!isPaid && (inv.invoice_type === "partial" || inv.invoice_type === "follow_up") && (
                      <div className="inv-vm-partial-notice">
                        <div className="inv-vm-partial-notice-title">
                          ⚡ {inv.invoice_type === "follow_up" ? "Follow-up Invoice" : "Partial Invoice"} — Payment Due Now
                        </div>
                        <div style={{ fontSize: 13, color: "#6B6762", marginTop: 4 }}>
                          This invoice covers a partial amount of the total order. The remaining balance will be invoiced in a future billing cycle.
                        </div>
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#D97706" }}>ORDER TOTAL</div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: "#D97706" }}>{fmtCurrency(salesOrderTotal, d)}</div>
                          <div style={{ fontSize: 13, color: "#D97706" }}>
                            Due Now: <strong>{fmtCurrency(balanceDue, d)}</strong>
                            {salesOrderTotal > amountPaid + balanceDue && (
                              <span style={{ marginLeft: 12, color: "#92400E" }}>| Remaining after this: <strong>{fmtCurrency(salesOrderTotal - amountPaid - balanceDue, d)}</strong></span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <table className="inv-vm-items-table">
                      <thead>
                        <tr>
                          <th>ITEM</th>
                          <th>SKU</th>
                          <th>QTY</th>
                          <th>UNIT</th>
                          <th>UNIT PRICE</th>
                          <th>TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailServices.length > 0 ? detailServices.map((s, i) => {
                          const itemType = s.item_type || "service";
                          if (itemType === "section") {
                            return (
                              <tr key={i}>
                                <td colSpan={6} style={{ fontWeight: 700, color: "#1A1917", fontSize: 12, background: "#F7F6F5", padding: "8px 10px" }}>
                                  {s.service_name || s.description || s.title || "—"}
                                </td>
                              </tr>
                            );
                          }
                          const name = s.service_name || s.description || s.title || "—";
                          const sku = s.sku || "—";
                          const qty = Number(s.quantity) || 1;
                          const unit = s.service_unit || s.unit || "—";
                          const price = Number(s.service_price || s.unit_price || s.price || 0);
                          const total = price * qty;
                          return (
                            <tr key={i}>
                              <td>
                                <div style={{ fontWeight: 600, fontSize: 13 }}>{name}</div>
                                {s.description && s.description !== name && (
                                  <div style={{ fontSize: 11, color: "#8A94AA", marginTop: 2 }}>{s.description}</div>
                                )}
                              </td>
                              <td style={{ color: "#6B6762", fontSize: 12 }}>{sku}</td>
                              <td>{qty}</td>
                              <td style={{ color: "#6B6762", fontSize: 12 }}>{unit}</td>
                              <td>{fmtCurrency(price, d)}</td>
                              <td style={{ fontWeight: 600 }}>{fmtCurrency(total, d)}</td>
                            </tr>
                          );
                        }) : (
                          <tr>
                            <td colSpan={6} style={{ textAlign: "center", color: "#9A9690", fontSize: 13, fontStyle: "italic", padding: "18px 0" }}>
                              No line items on record
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={5} style={{ textAlign: "right", color: "#6B6762", paddingRight: 12 }}>Subtotal</td>
                          <td style={{ fontWeight: 600 }}>{fmtCurrency(inv.sub_total, d)}</td>
                        </tr>
                        {detailDiscounts.map((disc, i) =>
                          Number(disc.discount_total || disc.applied_amount || 0) > 0 && (
                            <tr key={`disc-${i}`}>
                              <td colSpan={5} style={{ textAlign: "right", color: "#6B6762", paddingRight: 12 }}>
                                {disc.discount_reason || "Discount"} (−)
                              </td>
                              <td style={{ color: "#16a34a" }}>
                                {fmtCurrency(disc.discount_total || disc.applied_amount || 0, d)}
                              </td>
                            </tr>
                          )
                        )}
                        {detailTaxes.map((tax, i) =>
                          Number(tax.tax_total || tax.applied_amount || 0) > 0 && (
                            <tr key={`tax-${i}`}>
                              <td colSpan={5} style={{ textAlign: "right", color: "#6B6762", paddingRight: 12 }}>
                                {tax.tax_reason || "Tax"} (+)
                              </td>
                              <td style={{ color: "#1A1917" }}>
                                {fmtCurrency(tax.tax_total || tax.applied_amount || 0, d)}
                              </td>
                            </tr>
                          )
                        )}
                        {detailShipping.map((ship, i) =>
                          Number(ship.shipping_value || ship.applied_amount || 0) > 0 && (
                            <tr key={`ship-${i}`}>
                              <td colSpan={5} style={{ textAlign: "right", color: "#6B6762", paddingRight: 12 }}>
                                {ship.shipping_title || "Shipping & Handling"} (+)
                              </td>
                              <td style={{ color: "#1A1917" }}>
                                {fmtCurrency(ship.shipping_value || ship.applied_amount || 0, d)}
                              </td>
                            </tr>
                          )
                        )}
                        <tr>
                          <td colSpan={5} style={{ textAlign: "right", fontWeight: 700, color: "#1A1917", paddingRight: 12, borderTop: "2px solid #E5E0D5" }}>Grand Total</td>
                          <td style={{ fontWeight: 700, color: "#1A1917", borderTop: "2px solid #E5E0D5" }}>{fmtCurrency(grandTotal, d)}</td>
                        </tr>
                      </tfoot>
                    </table>

                    <div className="inv-vm-pdf-footer-grid">
                      <div>
                        {inv.notes ? (
                          <>
                            <div className="inv-vm-pdf-block-label">NOTES</div>
                            <div className="inv-vm-pdf-notes-content" dangerouslySetInnerHTML={{ __html: inv.notes }} />
                          </>
                        ) : null}
                      </div>
                      <div>
                        <div className="inv-vm-pdf-block-label">SUMMARY</div>
                        <div className="inv-vm-pdf-summary-table">
                          <div className="inv-vm-pdf-sum-row">
                            <span>Sub Total</span>
                            <span>{fmtCurrency(inv.sub_total, d)}</span>
                          </div>
                          {detailDiscounts.map((disc, i) =>
                            Number(disc.discount_total || disc.applied_amount || 0) > 0 && (
                              <div key={`disc-${i}`} className="inv-vm-pdf-sum-row green">
                                <span>{disc.discount_reason || "Discount"}</span>
                                <span>−{fmtCurrency(disc.discount_total || disc.applied_amount || 0, d)}</span>
                              </div>
                            )
                          )}
                          {detailTaxes.map((tax, i) =>
                            Number(tax.tax_total || tax.applied_amount || 0) > 0 && (
                              <div key={`tax-${i}`} className="inv-vm-pdf-sum-row">
                                <span>{tax.tax_reason || "Tax"}</span>
                                <span>{fmtCurrency(tax.tax_total || tax.applied_amount || 0, d)}</span>
                              </div>
                            )
                          )}
                          {detailShipping.map((ship, i) =>
                            Number(ship.shipping_value || ship.applied_amount || 0) > 0 && (
                              <div key={`ship-${i}`} className="inv-vm-pdf-sum-row">
                                <span>{ship.shipping_title || "Shipping & Handling"}</span>
                                <span>{fmtCurrency(ship.shipping_value || ship.applied_amount || 0, d)}</span>
                              </div>
                            )
                          )}
                          <div className="inv-vm-pdf-sum-row bold">
                            <span>Grand Total</span>
                            <span>{fmtCurrency(grandTotal, d)}</span>
                          </div>
                          {Number(inv.credit_applied) > 0 && (
                            <div className="inv-vm-pdf-sum-row">
                              <span>Credit Applied</span>
                              <span>{fmtCurrency(inv.credit_applied, d)}</span>
                            </div>
                          )}
                          <div className="inv-vm-pdf-sum-row">
                            <span>Paid Amount</span>
                            <span>{fmtCurrency(amountPaid, d)}</span>
                          </div>
                          <div className="inv-vm-pdf-sum-row bold">
                            <span>Due Amount</span>
                            <span>{fmtCurrency(balanceDue, d)}</span>
                          </div>
                        </div>
                        {!isPaid && (
                          <div className="inv-vm-pdf-pay-notice">
                            Payment due by {fmtDate(inv.due_date)} · {inv.payment_terms || "Net 30"}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* How to Pay — reseller's configured payment method (unpaid only) */}
                    {!isPaid && this.state.paymentDetails && (
                      <div className="inv-vm-pay-instructions">
                        <div className="inv-vm-pdf-block-label">How to Pay</div>
                        <div dangerouslySetInnerHTML={{ __html: this.state.paymentDetails }} />
                      </div>
                    )}

                  </div>
                  {this.renderSidebar(inv)}
                </div>
              )}

              {/* ── Admin Summary tab ─────────────────────────────── */}
              {viewModalTab === "admin" && (
                <div className="inv-vm-admin-tab">
                  <div className="inv-vm-admin-title">Admin Summary — {inv.invoice_no}</div>
                  <div className="inv-vm-admin-grid">
                    {[
                      { label: "INVOICE #",      value: inv.invoice_no },
                      { label: "ORDER #",        value: inv.orderno || "—" },
                      { label: "CLIENT",         value: inv.customer_name || "—" },
                      { label: "EMAIL",          value: inv.customer_email || "—" },
                      { label: "ISSUED",         value: fmtDate(inv.invoice_date) },
                      { label: "DUE DATE",       value: fmtDate(inv.due_date), red: !isPaid && isOverdueInv },
                      { label: "BILLING PERIOD", value: inv.billing_period || "—" },
                      { label: "SUBTOTAL",       value: fmtCurrency(inv.sub_total, d) },
                      ...(Number(inv.discount_total) > 0 ? [{ label: "DISCOUNT", value: `−${fmtCurrency(inv.discount_total, d)}` }] : []),
                      ...(Number(inv.tax_total) > 0      ? [{ label: "TAX",      value: fmtCurrency(inv.tax_total, d) }]               : []),
                      ...(Number(inv.shipping_total) > 0 ? [{ label: "SHIPPING", value: fmtCurrency(inv.shipping_total, d) }]           : []),
                      { label: "GRAND TOTAL",    value: fmtCurrency(grandTotal, d) },
                      ...(Number(inv.credit_applied) > 0 ? [{ label: "CREDIT APPLIED", value: fmtCurrency(inv.credit_applied, d) }] : []),
                      { label: "PAID AMOUNT",           value: fmtCurrency(amountPaid, d) },
                      { label: "DUE AMOUNT",     value: fmtCurrency(balanceDue, d), orange: balanceDue > 0 },
                    ].map((item) => (
                      <div key={item.label} className="inv-vm-admin-cell">
                        <div className="inv-vm-admin-cell-label">{item.label}</div>
                        <div className={`inv-vm-admin-cell-value${item.green ? " green" : item.orange ? " orange" : item.red ? " red" : ""}`}>
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                  {inv.notes && (
                    <div className="inv-vm-admin-note">"{inv.notes}"</div>
                  )}
                  <div className="inv-vm-admin-actions">
                    {!isPaid && (
                      <button className="btn btn-success" onClick={() => this.openPayModal(inv)}>
                        ✓ Record Payment
                      </button>
                    )}
                    <button className="btn btn-primary" onClick={() => this.downloadInvoicePdf(inv)}>
                      ↓ Download PDF
                    </button>
                    {this.props.onFollowUp && (
                      <button className="btn btn-secondary" onClick={this.handleFollowUp}>
                        + Follow-up Invoice
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── Payment Records tab ───────────────────────────── */}
              {viewModalTab === "payments" && (
                <div className="inv-vm-payments-tab">
                  <div className="inv-vm-admin-title">Payment Records — {inv.invoice_no}</div>
                  {payHistory.length === 0 ? (
                    <div className="inv-vm-payments-empty">No payment records found for this invoice.</div>
                  ) : (
                    <table className="inv-vm-payments-table">
                      <thead>
                        <tr>
                          <th>PAYMENT DATE</th>
                          <th>AMOUNT</th>
                          <th>CREDIT</th>
                          <th>METHOD</th>
                          <th>REFERENCE</th>
                          <th>STATUS AFTER</th>
                          <th>CREATED BY</th>
                          <th>DATE CREATED</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payHistory.map((p, i) => (
                          <tr key={i}>
                            <td>{fmtDate(p.payment_date)}</td>
                            <td style={{ fontWeight: 600, color: "#1A1917" }}>{fmtCurrency(p.payment_amount, d)}</td>
                            <td style={{ color: "#9A9690" }}>
                              {Number(p.credit_applied) > 0 ? fmtCurrency(p.credit_applied, d) : "—"}
                            </td>
                            <td style={{ color: "#6B6762" }}>{p.payment_method || "—"}</td>
                            <td style={{ color: "#6B6762", fontSize: 12 }}>{p.payment_reference || "—"}</td>
                            <td>
                              <span style={{
                                fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                                background: p.payment_status_after === "Paid" ? "rgba(22,163,74,0.1)" : "rgba(245,158,11,0.12)",
                                color: p.payment_status_after === "Paid" ? "#16a34a" : "#D97706",
                              }}>{p.payment_status_after || "—"}</span>
                            </td>
                            <td style={{ color: "#6B6762", fontSize: 11 }}>{p.recorded_by_name || "—"}</td>
                            <td style={{ color: "#9A9690", fontSize: 11 }}>{fmtDate(p.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

            </div>{/* end .inv-vm-body */}
          </div>
        </div>

        <RecordPaymentModal
          show={showPayModal}
          invoice={payInvoice}
          onClose={this.closePayModal}
          onSuccess={this.handlePaymentSuccess}
        />
        <NotificationToast
          message={this.state.toastMsg}
          type={this.state.toastType}
          onDismiss={() => this.setState({ toastMsg: "" })}
        />
      </>
    );
  }
}

export default ViewInvoiceModal;
